import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../api';
import Input from '../Atoms/Input';
import BlueButton from '../Atoms/BlueButton';
import Section from '../Atoms/Section';

const PageContainer = styled.div`
    max-width: 700px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Logo = styled.img`
    width: 200px;
    display: block;
    margin: 0 auto 30px;
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 10px;
    color: #1a1a1a;
`;

const Subtitle = styled.p`
    font-size: 16px;
    text-align: center;
    color: #666;
    margin-bottom: 40px;
    line-height: 1.5;
`;

const FormGroup = styled.div`
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 6px;
    }
`;

const ErrorMessage = styled.div`
    background: #fdecea;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    padding: 16px 20px;
    color: #721c24;
    text-align: center;
    margin-bottom: 20px;
    font-size: 15px;
`;

const SuccessMessage = styled.div`
    background: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 8px;
    padding: 20px;
    color: #155724;
    text-align: center;
    margin: 40px 0;

    h3 {
        margin-bottom: 10px;
    }
`;

const InstitutionnelSignupPage = (props) => {
    const { i18n } = useTranslation('common');
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        const form = e.target;
        const password = form.password.value;
        const passwordConfirm = form.passwordConfirm.value;

        if (password !== passwordConfirm) {
            setError(i18n.language === 'fr' ? "Les mots de passe ne correspondent pas" : "Passwords do not match");
            setSubmitting(false);
            return;
        }

        try {
            await api.RegisterInstitutionnel({
                email: form.email.value,
                password,
                firstname: form.firstname.value,
                lastname: form.lastname.value,
                societe: form.societe.value,
                phone: form.phone?.value || '',
                language: i18n.language
            });

            setSuccess(true);
            if (props.refreshUser) props.refreshUser();

            setTimeout(() => {
                navigate('/annuaire');
            }, 2000);

        } catch (err) {
            setError(err.response?.data?.error || (i18n.language === 'fr' ? "Erreur lors de l'inscription" : "Registration error"));
            setSubmitting(false);
        }
    };

    return (
        <PageContainer>
            <Logo src="/img/LogosV3.png" alt="Le French Design" />

            <Title>
                {i18n.language === 'fr' ? 'Inscription Institutionnel' : 'Institutional Registration'}
            </Title>
            <Subtitle>
                {i18n.language === 'fr'
                    ? "Créez votre compte pour accéder à l'annuaire de la communauté Le FRENCH DESIGN."
                    : "Create your account to access the Le FRENCH DESIGN community directory."}
            </Subtitle>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {success && (
                <SuccessMessage>
                    <h3>{i18n.language === 'fr' ? 'Compte créé !' : 'Account created!'}</h3>
                    <p>{i18n.language === 'fr'
                        ? "Redirection vers l'annuaire..."
                        : 'Redirecting to the directory...'}</p>
                </SuccessMessage>
            )}

            {!success && (
                <form onSubmit={handleSubmit}>
                    <Section className="padding">
                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Prénom *' : 'First name *'}</label>
                            <Input
                                name="firstname"
                                required
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Prénom' : 'First name'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Nom *' : 'Last name *'}</label>
                            <Input
                                name="lastname"
                                required
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Nom' : 'Last name'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Email *</label>
                            <Input
                                name="email"
                                type="email"
                                required
                                style={{ width: '100%' }}
                                placeholder="email@example.com"
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Structure *' : 'Structure *'}</label>
                            <Input
                                name="societe"
                                required
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Nom de votre structure' : 'Structure name'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Téléphone' : 'Phone'}</label>
                            <Input
                                name="phone"
                                type="tel"
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Optionnel' : 'Optional'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Mot de passe *' : 'Password *'}</label>
                            <Input
                                name="password"
                                type="password"
                                required
                                minLength={6}
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Minimum 6 caractères' : 'Minimum 6 characters'}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Confirmer le mot de passe *' : 'Confirm password *'}</label>
                            <Input
                                name="passwordConfirm"
                                type="password"
                                required
                                minLength={6}
                                style={{ width: '100%' }}
                                placeholder={i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                            />
                        </FormGroup>

                        <div style={{ marginTop: 10, marginBottom: 20 }}>
                            <Input type="checkbox" required />&nbsp;&nbsp;
                            <span style={{ fontSize: 13 }}>
                                {i18n.language === 'fr' ? "J'ai lu et j'accepte " : "I have read and accept "}
                                <a href='/documents/cvg-cgu.pdf' target='_blank' rel="noreferrer">
                                    {i18n.language === 'fr' ? "les conditions générales." : "the terms and conditions."}
                                </a>
                            </span>
                        </div>

                        <BlueButton
                            className="centered"
                            style={{ width: '100%', opacity: submitting ? 0.6 : 1 }}
                            disabled={submitting}
                        >
                            {submitting
                                ? (i18n.language === 'fr' ? 'Création en cours...' : 'Creating...')
                                : (i18n.language === 'fr' ? 'Créer mon compte' : 'Create my account')
                            }
                        </BlueButton>
                    </Section>
                </form>
            )}
        </PageContainer>
    );
};

export default InstitutionnelSignupPage;
